import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import { CgWebsite } from "react-icons/cg";
import { BsGithub } from "react-icons/bs";
import Particle from "../Particle";
import FeaturedProjectsSlider from "./FeaturedProjectsSlider";
import researchFig from "../../Assets/research-fig2.png";
import { SiArxiv } from "react-icons/si";
import { AiOutlineLineChart, AiOutlineRobot } from "react-icons/ai";
import weather from "../../Assets/weather-app.png";
import quiz from "../../Assets/quiz-app.png";
import editor from "../../Assets/video-flip.png";
import docChat from "../../Assets/doc-chat.png";
import quizzing from "../../Assets/study-quiz.png";
import chatbot from "../../Assets/chatbot.png";
import compare from "../../Assets/job-market.png";
import quizzy from "../../Assets/quizzy.png";
import flappy from "../../Assets/flappy-bird.png";


const ProjectPlaceholder = ({ icon: Icon, title }) => (
  <div className="project-placeholder">
    {Icon && <Icon className="project-placeholder-icon" />}
    <span>{title}</span>
  </div>
);

const ProjectCard = ({ imgPath, icon, title, description, ghLink, demoLink, paperLink, isBlog, techStack }) => {
  return (
    <Card className="project-card-view">
      {imgPath ? (
        <Card.Img variant="top" src={imgPath} alt={`${title} screenshot`} />
      ) : (
        <ProjectPlaceholder icon={icon} title={title} />
      )}
      <Card.Body>
        <Card.Title>{title}</Card.Title>
        <Card.Text style={{ textAlign: "justify" }}>{description}</Card.Text>

        {/* Tech Stack Badges */}
        {techStack && techStack.length > 0 && (
          <div style={{ marginBottom: "15px", display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {techStack.map((tech, index) => (
              <span
                key={index}
                style={{
                  backgroundColor: "rgba(199, 112, 240, 0.15)",
                  color: "#c770f0",
                  padding: "4px 12px",
                  borderRadius: "15px",
                  fontSize: "0.85rem",
                  fontWeight: "500",
                  border: "1px solid rgba(199, 112, 240, 0.3)"
                }}
              >
                {tech}
              </span>
            ))}
          </div>
        )}

        {paperLink && (
          <Button variant="primary" href={paperLink} target="_blank" style={{ marginRight: "10px" }}>
            <SiArxiv /> &nbsp;Paper
          </Button>
        )}
        {ghLink && (
          <Button variant="primary" href={ghLink} target="_blank">
            <BsGithub /> &nbsp;
            {isBlog ? "Blog" : "GitHub"}
          </Button>
        )}
        {!isBlog && demoLink && (
          <Button
            variant="primary"
            href={demoLink}
            target="_blank"
            style={{ marginLeft: "10px" }}
          >
            <CgWebsite /> &nbsp;
            {"Demo"}
          </Button>
        )}
        {!ghLink && !paperLink && !demoLink && (
          <span className="project-private-note">Code private, happy to walk through it</span>
        )}
      </Card.Body>
    </Card>
  );
};

function Projects() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    let frame = null;
    const handleMouseMove = (e) => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        setMousePosition({ x: e.clientX, y: e.clientY });
        frame = null;
      });
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  const shownProjects = [
    {
      imgPath: researchFig,
      title: "AI Safety Research Paper",
      description: "Sole-author paper: across 12 frontier models, a professional-looking data panel makes AI agents answer questions nobody can know (6.5% to 54%), and fabricated data works as well as real data. I also trained a 3B model that learns to decline instead.",
      paperLink: "https://arxiv.org/abs/2608.27167",
      ghLink: "https://github.com/Pranav-1100/confidence-calibration-evaluation",
      techStack: ["PyTorch", "QLoRA", "SFT + DPO", "LLM Evaluation", "Statistics"]
    },
    {
      icon: AiOutlineRobot,
      title: "EchoMind",
      description: "A retrieval-augmented personal AI assistant that studies long-term context retention and 'context pollution' hallucinations, with a multi-agent orchestrator routing between scheduling and retrieval tools.",
      ghLink: "https://github.com/Pranav-1100/EchoMind",
      techStack: ["RAG", "Pinecone", "Multi-Agent", "LLMs"]
    },
    {
      imgPath: chatbot,
      title: "Chatbot App",
      description: "Multi-LLM chatbot builder with custom prompts, training data, and conversation tagging. Auto-selects best LLM based on query. Includes hotel booking chatbot with 95% accuracy.",
      ghLink: "https://github.com/Pranav-1100/chatbot",
      demoLink: "https://chatbot-1-woad.vercel.app/",
      loomLink: "https://www.loom.com/share/ad3a9f85d9524af6bb23d52cde2c727d?sid=624db817-3b3a-4b57-af37-d56751da7c39",
      techStack: ["React", "Express.js", "SQLite", "OpenAI API", "Multi-LLM", "MCP"]
    },
    {
      imgPath: docChat,
      title: "Doc-Chat",
      description: "An AI-powered document interaction tool. Upload various document types to train a personal chatbot, ask questions, get summaries, and receive responses via HTTP streaming.",
      ghLink: "https://github.com/Pranav-1100/document-frontend",
      demoLink: "https://document-frontend-1.vercel.app/",
      loomLink: "https://www.loom.com/share/b43d4b5a05014967bb097417d599d84d?sid=907627a5-fb54-44ac-b018-c0563065d3f8",
      techStack: ["React", "Node.js", "OpenAI API", "RAG", "Vector Embeddings"]
    },
    {
      imgPath: compare,
      title: "Job Market",
      description: "A comprehensive job application platform. Upload resumes for company-specific scoring, view job listings, get customized resume suggestions, and prepare for interviews with tailored questions.",
      ghLink: "https://github.com/Pranav-1100/compare-frontend",
      demoLink: "https://compare-frontend-sigma.vercel.app/",
      loomLink: "https://www.loom.com/share/4d1aa4ba1c0242938aeb4073efaf4d40?sid=2363ff1e-1053-4f18-9104-4331da000445",
      techStack: ["React", "Node.js", "OpenAI API", "MongoDB"]
    }
  ];

  const moreProjects = [
    {
      icon: AiOutlineLineChart,
      title: "Walk-Forward RL Trading Study",
      description: "PPO implemented from scratch (manual backprop and Adam, no framework), trained per market regime and tested out-of-sample, judged with Deflated Sharpe Ratio and PBO. It found a clean null result, which led to my research paper.",
      techStack: ["Python", "Reinforcement Learning", "PPO", "Statistics"]
    },
    {
      imgPath: quizzing,
      title: "Study-Quiz App",
      description: "A comprehensive study platform for exam preparation. Features include quizzes, note-taking, doubt-solving, study plan creation, and content sharing among users.",
      ghLink: "https://github.com/Pranav-1100/quizzing-frontend",
      demoLink: "https://quizzing-frontend.vercel.app/",
      loomLink: "https://www.loom.com/share/ea7f4cf3b86b4a4ebbd23c2522725d1d?sid=82c0943c-a4d5-40c8-9eea-67f0c7dc5829",
      techStack: ["React", "Express.js", "MongoDB", "Node.js"]
    },
    {
      imgPath: quizzy,
      title: "Quizzy-App",
      description: "An advanced quiz application featuring a variety of question types and difficulty levels. Offers a fun and challenging way to test and improve knowledge on different subjects.",
      ghLink: "https://github.com/Pranav-1100/quiz_app_frontend",
      demoLink: "https://web-nine-snowy.vercel.app/",
      loomLink: "https://www.loom.com/share/0cb719585075485a9a9c9dd875f7f3da?sid=990f0742-f04e-415c-b620-cd9e3e06e3ea",
      techStack: ["React", "Express.js", "MongoDB"]
    }
  ];

  const smallerProjects = [
    {
      imgPath: editor,
      title: "Video-Flip Editor",
      description: "A versatile video editing tool offering basic functionalities such as cropping, speed adjustment, volume control, and live preview recording. Perfect for quick video edits on-the-go.",
      ghLink: "https://github.com/Pranav-1100/video-editor",
      demoLink: "https://video-editor-eosin.vercel.app/",
      techStack: ["JavaScript", "HTML5", "CSS3", "Web APIs"]
    },
    {
      imgPath: weather,
      title: "Weather-App",
      description: "A sleek and intuitive weather application providing real-time updates. Users can search for any city globally to receive instant, accurate weather information.",
      ghLink: "https://github.com/Pranav-1100/weather-app",
      demoLink: "https://custom-weather-react.vercel.app",
      techStack: ["React", "Weather API", "JavaScript"]
    },
    {
      imgPath: quiz,
      title: "Quiz-App",
      description: "An engaging quiz application designed to challenge users' knowledge across various topics. Features a user-friendly interface and a diverse range of questions.",
      ghLink: "https://github.com/Pranav-1100/Quiz",
      demoLink: "https://quiz-github123.vercel.app/",
      techStack: ["React", "JavaScript", "CSS3"]
    },
    {
      imgPath: flappy,
      title: "Flappy Bird Game",
      description: "A recreation of the classic Flappy Bird game, offering addictive gameplay and nostalgic graphics. Test your reflexes and aim for the high score in this challenging side-scroller.",
      ghLink: "https://github.com/Pranav-1100/flappy-bird",
      demoLink: "https://flappy-bird-game-ruby.vercel.app/",
      techStack: ["JavaScript", "HTML5 Canvas", "Game Dev"]
    }
  ];

  return (
    <Container fluid className="project-section" 
      style={{
        background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(255,0,0,0.2), rgba(0,0,255,0.2), rgba(0,255,0,0.2))`,
        transition: 'background 0.3s ease'
      }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <Particle />
      <Container>
        <h1 className="project-heading mb-5">
          Featured <strong className="purple">Projects</strong>
        </h1>
        <FeaturedProjectsSlider projects={shownProjects} />
        
        {[
          { heading: <>More <strong className="purple">Projects</strong></>, items: moreProjects },
          { heading: <>Smaller <strong className="purple">Projects</strong></>, items: smallerProjects },
        ].map((group, gi) => (
          <React.Fragment key={gi}>
            <h1 className="project-heading mt-5" style={{ marginBottom: "2rem" }}>{group.heading}</h1>
            <Row style={{ justifyContent: "center", paddingBottom: "10px" }}>
              {group.items.map((project, index) => (
                <Col xs={12} md={4} className="project-card" key={index}>
                  <div className="project-card-wrapper"
                    style={{
                      transform: isHovering ? `perspective(1000px) rotateY(${(mousePosition.x / window.innerWidth - 0.5) * 20}deg) rotateX(${(mousePosition.y / window.innerHeight - 0.5) * -20}deg)` : 'none',
                      transition: 'transform 0.5s ease'
                    }}
                  >
                    <ProjectCard {...project} />
                  </div>
                </Col>
              ))}
            </Row>
          </React.Fragment>
        ))}
      </Container>
    </Container>
  );
}

export default Projects;